import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  User,
  Heart,
  ShoppingBag,
  Menu,
  ChevronDown,
  ArrowRight,
  Sparkles,
  X,
  Mail,
  Lock,
} from "lucide-react";

import serumImg from "../assests/faceimg.webp"; // Path check karein

const Navbar = () => {
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCollectionsOpen, setIsMobileCollectionsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Auth Pop-up State
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // User State
  const [user, setUser] = useState<{ name: string } | null>(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      setUser(null);
    }
  }, []);

  const handleUserClick = () => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/profile");
    } else {
      setIsAuthModalOpen(true);
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");

    const endpoint = authMode === "signup" ? "/api/auth/signup" : "/api/auth/login";

    try {
      const res = await fetch(`http://localhost:1010${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setUser(data.user);
        setIsAuthModalOpen(false);
        navigate("/profile");
      } else {
        setErrorMsg(data.message || "Authentication failed");
      }
    } catch (err) {
      setErrorMsg("Backend server is not reachable on port 1010.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-[#F7F4EF]/95 backdrop-blur-md border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto h-20 px-6 flex items-center justify-between">
          
          {/* Logo */}
          <Link to="/" className="cursor-pointer group">
            <h1 className="font-heading text-3xl tracking-[4px] text-[#1F1B18] transition">
              CELIORA
            </h1>
            <p className="text-[10px] uppercase tracking-[3px] text-[#7B736C]">
              Seoul Inspired Skincare
            </p>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-9">
            <Link to="/" className="nav-item">Home</Link>
            <Link to="/shop" className="nav-item">Shop</Link>

            {/* Mega Menu Dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-1.5 py-6 text-sm tracking-widest uppercase font-medium hover:text-[#9D7D4F] transition-colors">
                Collections
                <ChevronDown size={15} className="transition-transform duration-300 group-hover:rotate-180 text-[#7B736C]" />
              </button>

              <div className="invisible opacity-0 translate-y-3 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 ease-out absolute top-full left-1/2 -translate-x-1/2 w-[820px] rounded-2xl bg-[#F7F4EF]/95 backdrop-blur-xl shadow-2xl border border-[#E8E2D9] p-6 z-50">
                <div className="grid grid-cols-12 gap-6">
                  
                  {/* Column 1: Categories */}
                  <div className="col-span-4 border-r border-[#E8E2D9]/60 pr-4">
                    <span className="text-[11px] font-semibold tracking-[2px] text-[#9D7D4F] uppercase block mb-4">Categories</span>
                    <div className="space-y-2.5">
                      <Link to="/collections/serums" className="dropdown-item"><span>Serums & Oils</span><ArrowRight size={14} className="opacity-0 -translate-x-2 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all" /></Link>
                      <Link to="/collections/moisturizers" className="dropdown-item"><span>Moisturizers</span><ArrowRight size={14} className="opacity-0 -translate-x-2 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all" /></Link>
                      <Link to="/collections/barrier-cream" className="dropdown-item"><span>Barrier Creams</span><ArrowRight size={14} className="opacity-0 -translate-x-2 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all" /></Link>
                      <Link to="/collections/cleansers" className="dropdown-item"><span>Cleansers & Toners</span><ArrowRight size={14} className="opacity-0 -translate-x-2 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all" /></Link>
                    </div>
                  </div>

                  {/* Column 2: 2 Trending Products Restored */}
                  <div className="col-span-4 border-r border-[#E8E2D9]/60 pr-4">
                    <span className="text-[11px] font-semibold tracking-[2px] text-[#9D7D4F] uppercase block mb-4">Trending Products</span>
                    <div className="space-y-3">
                      <Link to="/product/glow-serum" className="flex items-center gap-3 p-2 rounded-xl hover:bg-[#FAF8F5] transition group/prod">
                        <div className="w-12 h-12 rounded-lg bg-[#F7F4EF] overflow-hidden flex-shrink-0">
                          <img src={serumImg} alt="Glow Serum" className="w-full h-full object-cover group-hover/prod:scale-110 transition duration-300" />
                        </div>
                        <div>
                          <h5 className="text-xs font-semibold text-[#1F1B18] group-hover/prod:text-[#9D7D4F] transition">Glow Niacinamide Serum</h5>
                          <p className="text-[11px] text-gray-500">$48.00</p>
                        </div>
                      </Link>

                      <Link to="/product/barrier-cream" className="flex items-center gap-3 p-2 rounded-xl hover:bg-[#FAF8F5] transition group/prod">
                        <div className="w-12 h-12 rounded-lg bg-[#F7F4EF] overflow-hidden flex-shrink-0">
                          <img src={serumImg} alt="Ceramide Cream" className="w-full h-full object-cover group-hover/prod:scale-110 transition duration-300" />
                        </div>
                        <div>
                          <h5 className="text-xs font-semibold text-[#1F1B18] group-hover/prod:text-[#9D7D4F] transition">Ceramide Hydrating Cream</h5>
                          <p className="text-[11px] text-gray-500">$52.00</p>
                        </div>
                      </Link>
                    </div>
                  </div>

                  {/* Column 3: Banner */}
                  <div className="col-span-4">
                    <Link to="/collections/seoul-edition" className="relative block h-full min-h-[220px] rounded-xl overflow-hidden group/card shadow-sm">
                      <img src={serumImg} alt="Seoul Collection" className="absolute inset-0 w-full h-full object-cover group-hover/card:scale-105 transition duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-4 flex flex-col justify-end text-white">
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest bg-white/20 backdrop-blur-md px-2 py-0.5 rounded-full w-max mb-1"><Sparkles size={10} /> New Arrival</span>
                        <h4 className="font-serif text-base font-medium leading-tight">Seoul Radiant Routine</h4>
                      </div>
                    </Link>
                  </div>

                </div>
              </div>
            </div>

            <Link to="/best-sellers" className="nav-item">Best Sellers</Link>
            <Link to="/about" className="nav-item">About</Link>
            <Link to="/contact" className="nav-item">Contact</Link>
          </nav>

          {/* Action Icons */}
          <div className="hidden lg:flex items-center gap-5 text-[#1F1B18]">
            <button onClick={() => setIsSearchOpen(true)} className="hover:text-[#9D7D4F] hover:scale-110 transition"><Search size={19} /></button>

            {/* Logged in check for Icon / Profile Button */}
            {user ? (
              <button onClick={handleUserClick} className="flex items-center gap-2 bg-[#FAF8F5] border border-[#E8E2D9] px-3 py-1.5 rounded-full text-xs font-medium text-[#1F1B18] hover:border-[#9D7D4F] transition shadow-2xs">
                <div className="w-5 h-5 rounded-full bg-[#9D7D4F] text-white flex items-center justify-center text-[10px] font-bold">
                  {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                </div>
                <span>Profile</span>
              </button>
            ) : (
              <button onClick={handleUserClick} className="hover:text-[#9D7D4F] hover:scale-110 transition"><User size={19} /></button>
            )}

            <Link to="/wishlist" className="hover:text-[#9D7D4F] hover:scale-110 transition"><Heart size={19} /></Link>
            <Link to="/cart" className="relative hover:text-[#9D7D4F] hover:scale-110 transition">
              <ShoppingBag size={19} />
              <span className="absolute -top-1.5 -right-2 bg-[#9D7D4F] text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold">2</span>
            </Link>
          </div>

          {/* Mobile Navigation Trigger */}
          <div className="flex lg:hidden items-center gap-4 text-[#1F1B18]">
            <button onClick={() => setIsSearchOpen(true)}><Search size={20} /></button>
            <button onClick={handleUserClick}><User size={20} /></button>
            <Link to="/cart"><ShoppingBag size={21} /></Link>
            <button onClick={() => setIsMobileMenuOpen(true)}><Menu size={22} /></button>
          </div>

        </div>
      </header>

      {/* Mobile Drawer */}
      <div onClick={() => setIsMobileMenuOpen(false)} className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[80] lg:hidden transition-opacity duration-300 ${isMobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`} />
      <aside className={`fixed top-0 left-0 h-full w-[85%] max-w-xs bg-[#FAF8F5] z-[90] lg:hidden shadow-2xl transition-transform duration-300 ease-in-out flex flex-col justify-between ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div>
          <div className="p-6 border-b border-[#E8E2D9] flex items-center justify-between">
            <h2 className="font-heading text-xl tracking-[3px] text-[#1F1B18]">CELIORA</h2>
            <button onClick={() => setIsMobileMenuOpen(false)}><X size={20} /></button>
          </div>
          <div className="p-6 flex flex-col gap-5 text-sm uppercase tracking-widest font-medium text-[#1F1B18]">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
            <Link to="/shop" onClick={() => setIsMobileMenuOpen(false)}>Shop</Link>
            <div>
              <button onClick={() => setIsMobileCollectionsOpen(!isMobileCollectionsOpen)} className="flex items-center justify-between w-full uppercase tracking-widest">
                <span>Collections</span>
                <ChevronDown size={16} className={`transition-transform ${isMobileCollectionsOpen ? "rotate-180" : ""}`} />
              </button>
              {isMobileCollectionsOpen && (
                <div className="mt-3 ml-3 flex flex-col gap-3 text-xs tracking-wider normal-case text-gray-600 border-l-2 border-[#9D7D4F] pl-3">
                  <Link to="/collections/serums" onClick={() => setIsMobileMenuOpen(false)}>Serums & Oils</Link>
                  <Link to="/collections/moisturizers" onClick={() => setIsMobileMenuOpen(false)}>Moisturizers</Link>
                  <Link to="/collections/barrier-cream" onClick={() => setIsMobileMenuOpen(false)}>Barrier Creams</Link>
                </div>
              )}
            </div>
            <Link to="/best-sellers" onClick={() => setIsMobileMenuOpen(false)}>Best Sellers</Link>
            <Link to="/about" onClick={() => setIsMobileMenuOpen(false)}>About</Link>
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>Contact</Link>
          </div>
        </div>
      </aside>

      {/* Side Search Drawer with Popular Search Tags Restored */}
      <div onClick={() => setIsSearchOpen(false)} className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] transition-opacity duration-300 ${isSearchOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`} />
      <aside className={`fixed top-0 right-0 h-full w-full max-w-md bg-white z-[70] shadow-2xl transition-transform duration-300 flex flex-col ${isSearchOpen ? "translate-x-0" : "translate-x-full"}`}>
        <div className="p-6 border-b border-[#E8E2D9] flex items-center justify-between">
          <h3 className="text-lg font-medium text-[#1F1B18]">Search Our Store</h3>
          <button onClick={() => setIsSearchOpen(false)}><X size={22} /></button>
        </div>
        <div className="p-6 border-b border-[#E8E2D9]/60">
          <input type="text" placeholder="Search products, ingredients..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-3 px-4 text-sm focus:outline-none focus:border-[#9D7D4F]" />
        </div>
        <div className="p-6 overflow-y-auto flex-1">
          {searchQuery.trim() === "" ? (
            <div>
              <p className="text-xs uppercase tracking-wider text-[#9D7D4F] font-semibold mb-3">Popular Searches</p>
              <div className="flex flex-wrap gap-2">
                {["Niacinamide", "Barrier Cream", "Serum", "Hydrating", "Cleanser"].map((tag) => (
                  <button key={tag} onClick={() => setSearchQuery(tag)} className="text-xs bg-[#FAF8F5] hover:bg-[#9D7D4F] hover:text-white px-3 py-1.5 rounded-full border border-[#E8E2D9] transition">
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400">Results for "{searchQuery}"</p>
          )}
        </div>
      </aside>

      {/* Smooth Branded Auth Popup Modal */}
      {isAuthModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div onClick={() => setIsAuthModalOpen(false)} className="fixed inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-300" />
          <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-[#E8E2D9] overflow-hidden grid grid-cols-1 md:grid-cols-12 z-10 animate-in fade-in zoom-in-95 duration-200">
            <button onClick={() => setIsAuthModalOpen(false)} className="absolute top-4 right-4 z-20 text-gray-400 hover:text-black p-1.5 bg-white/80 rounded-full backdrop-blur-md shadow-sm">
              <X size={18} />
            </button>

            <div className="md:col-span-5 relative hidden md:block bg-[#F7F4EF] p-6">
              <img src={serumImg} alt="Celiora Luxury" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-end text-white">
                <span className="text-[10px] uppercase tracking-[2px] text-[#9D7D4F] font-semibold mb-1 flex items-center gap-1"><Sparkles size={11} /> Celiora VIP Club</span>
                <h4 className="font-serif text-xl">Seoul Skincare Rituals</h4>
              </div>
            </div>

            <div className="md:col-span-7 p-6 sm:p-8 flex flex-col justify-center">
              <div className="flex border-b border-[#E8E2D9] mb-6">
                <button type="button" onClick={() => { setAuthMode("login"); setErrorMsg(""); }} className={`pb-2 text-xs tracking-widest uppercase font-semibold relative ${authMode === "login" ? "text-[#1F1B18]" : "text-gray-400"}`}>
                  Sign In {authMode === "login" && <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[#9D7D4F]" />}
                </button>
                <button type="button" onClick={() => { setAuthMode("signup"); setErrorMsg(""); }} className={`pb-2 ml-6 text-xs tracking-widest uppercase font-semibold relative ${authMode === "signup" ? "text-[#1F1B18]" : "text-gray-400"}`}>
                  Create Account {authMode === "signup" && <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[#9D7D4F]" />}
                </button>
              </div>

              {errorMsg && <div className="mb-4 p-2.5 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs">{errorMsg}</div>}

              <form onSubmit={handleAuthSubmit} className="space-y-3.5">
                {authMode === "signup" && (
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-[#7B736C] font-semibold mb-1">Full Name</label>
                    <div className="relative">
                      <input type="text" required placeholder="Aanya Sharma" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-2.5 pl-9 pr-3 text-xs focus:outline-none focus:border-[#9D7D4F] text-[#1F1B18]" />
                      <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-[#7B736C] font-semibold mb-1">Email Address</label>
                  <div className="relative">
                    <input type="email" required placeholder="name@domain.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-2.5 pl-9 pr-3 text-xs focus:outline-none focus:border-[#9D7D4F] text-[#1F1B18]" />
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-[#7B736C] font-semibold mb-1">Password</label>
                  <div className="relative">
                    <input type="password" required placeholder="••••••••" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-2.5 pl-9 pr-3 text-xs focus:outline-none focus:border-[#9D7D4F] text-[#1F1B18]" />
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                <button type="submit" disabled={loading} className="w-full bg-[#1F1B18] text-white py-3 rounded-xl text-xs uppercase tracking-widest font-semibold hover:bg-[#9D7D4F] transition flex items-center justify-center gap-2 mt-2 shadow-md">
                  <span>{loading ? "Processing..." : authMode === "login" ? "Sign In" : "Register Account"}</span>
                  <ArrowRight size={14} />
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;